import React from 'react';
import { Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { LeadsPage } from './pages/LeadsPage';
import { ContactsPage } from './pages/ContactsPage';
import { OrganizationDetailPage } from './pages/OrganizationDetailPage';
import { ContactDetailPage } from './pages/ContactDetailPage';
import { LeadDetailPage } from './pages/LeadDetailPage';
import { SettingsPage } from './pages/SettingsPage';
import { DocumentLayoutEditorPage } from './features/settings/components/invoiceTemplate/DocumentLayoutEditorPage';
import { SETTINGS_PATH_BUSINESS } from './utils/settingsRoutes';
import { InboxPage } from './pages/InboxPage';
import { CalendarPage } from './pages/CalendarPage';
import { StammdatenPage } from './pages/StammdatenPage';
import { DashboardPage } from './pages/DashboardPage';
import { ChatPage } from './pages/ChatPage';
import { TemplatesPage } from './pages/TemplatesPage';
import { EmployeesPage } from './pages/EmployeesPage';
import { FuhrparkPage } from './pages/FuhrparkPage';
import { AufgabenPage } from './pages/AufgabenPage';
import { InvoicesPage } from './pages/InvoicesPage';
import { invoiceTabFromSlug, invoicesOverviewPath } from './utils/invoiceRoutes';
import { stammdatenDefaultPath, stammdatenTabFromSlug } from './utils/stammdatenRoutes';
import { AusschreibungenPage } from './pages/AusschreibungenPage';
import { AusschreibungDetailPage } from './pages/AusschreibungDetailPage';
import { LoginPage } from './pages/LoginPage';
import { AuthCallbackPage } from './pages/AuthCallbackPage';
import { AuthConfirmPage } from './pages/AuthConfirmPage';
import { CompleteProfilePage } from './pages/CompleteProfilePage';
import { ExpiredAuthLinkPage } from './pages/ExpiredAuthLinkPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { PendingApprovalPage } from './pages/PendingApprovalPage';
import { NotificationsPage } from './pages/NotificationsPage';
import { employeesOverviewPath } from './utils/employeeRoutes';
import { RouteDocumentTitle } from './components/RouteDocumentTitle';
import { CapabilityGate } from './components/CapabilityGate';
import { PageLoadingState } from './components/LoadingSpinner';
import { PdfMailShareProvider } from './features/inbox/PdfMailShareProvider';

function InstanceHomeRedirect() {
  const { instanceId } = useParams<{ instanceId: string }>();
  return <Navigate to={`/i/${encodeURIComponent(instanceId ?? 'default')}/dashboard`} replace />;
}

/** Alte Adresse /aufgaben[/…] → /todos[/…] (Ansicht und ?task= bleiben erhalten). */
function LegacyAufgabenRedirect() {
  const { instanceId, viewSlug } = useParams<{ instanceId: string; viewSlug?: string }>();
  const { search } = useLocation();
  const base = `/i/${encodeURIComponent(instanceId ?? 'default')}/todos`;
  return <Navigate to={`${viewSlug ? `${base}/${viewSlug}` : base}${search}`} replace />;
}

function EmployeesDefaultRedirect() {
  const { instanceId } = useParams<{ instanceId: string }>();
  return <Navigate to={employeesOverviewPath(instanceId ?? 'default', 'uebersicht')} replace />;
}

function EmployeesOverviewTabRedirect() {
  const { instanceId } = useParams<{ instanceId: string }>();
  return <Navigate to={employeesOverviewPath(instanceId ?? 'default', 'uebersicht')} replace />;
}

function InvoicesDefaultRedirect() {
  const { instanceId } = useParams<{ instanceId: string }>();
  return <Navigate to={invoicesOverviewPath(instanceId ?? 'default', 'outgoing')} replace />;
}

/** Old German URL → /i/.../invoices/... */
function StammdatenDefaultRedirect() {
  const { instanceId } = useParams<{ instanceId: string }>();
  return <Navigate to={stammdatenDefaultPath(instanceId ?? 'default')} replace />;
}

function StammdatenTabRedirect() {
  const { instanceId, tabSlug } = useParams<{ instanceId: string; tabSlug: string }>();
  const tab = stammdatenTabFromSlug(tabSlug);
  if (!tab) {
    return <Navigate to={stammdatenDefaultPath(instanceId ?? 'default')} replace />;
  }
  return <StammdatenPage />;
}

function LegacyRechnungenRedirect() {
  const { instanceId } = useParams<{ instanceId: string }>();
  return <Navigate to={invoicesOverviewPath(instanceId ?? 'default', 'outgoing')} replace />;
}

function LegacyRechnungenTabRedirect() {
  const { instanceId, tabSlug } = useParams<{ instanceId: string; tabSlug: string }>();
  const tab = invoiceTabFromSlug(tabSlug) ?? 'outgoing';
  return <Navigate to={invoicesOverviewPath(instanceId ?? 'default', tab)} replace />;
}

function ProjectsDefaultRedirect() {
  const { instanceId } = useParams<{ instanceId: string }>();
  const { search } = useLocation();
  return (
    <Navigate
      to={`/i/${encodeURIComponent(instanceId ?? 'default')}/projects/pipeline${search}`}
      replace
    />
  );
}

/** `/leads` → `/projects/pipeline` (bookmarks, CTAs). */
function LegacyLeadsListRedirect() {
  const { instanceId } = useParams<{ instanceId: string }>();
  const { search } = useLocation();
  return (
    <Navigate
      to={`/i/${encodeURIComponent(instanceId ?? 'default')}/projects/pipeline${search}`}
      replace
    />
  );
}

/** `/leads/:id` and `/leads/:id/:docType` → `/projects/...` */
function LegacyLeadsDetailRedirect() {
  const { instanceId, id, docType } = useParams<{ instanceId: string; id: string; docType?: string }>();
  const suffix = docType ? `/${encodeURIComponent(docType)}` : '';
  return (
    <Navigate
      to={`/i/${encodeURIComponent(instanceId ?? 'default')}/projects/${encodeURIComponent(id ?? '')}${suffix}`}
      replace
    />
  );
}

/** Remount project detail when `:id` changes so stale lead state cannot trigger a canonical-URL redirect ping-pong. */
function LeadDetailRoute() {
  const { id } = useParams<{ id: string }>();
  return <LeadDetailPage key={id ?? ''} />;
}

/** Shown to mobile-only (field_only) members who reach the web backoffice. */
function FieldOnlyNotice() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-app-bg px-6">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-bold text-app-text mb-2">Bitte die Meisterpfad App verwenden</h1>
        <p className="text-sm text-app-text-muted">
          Dein Zugang ist auf die mobile App beschränkt. Das Backoffice steht für dieses Konto nicht zur Verfügung.
        </p>
      </div>
    </div>
  );
}

import { fetchMe, getLastMeAuthFailureCode } from './lib/meClient';
import { hydrateColorSchemePreference } from './lib/colorSchemePreference';
import { loginPathWithNext } from './lib/postLoginRedirect';
import { CompanyTimezoneProvider } from './hooks/useCompanyTimezone';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { instanceId } = useParams<{ instanceId?: string }>();
  const location = useLocation();
  const [checked, setChecked] = React.useState(false);
  const [authed, setAuthed] = React.useState(false);
  const [fieldOnly, setFieldOnly] = React.useState(false);
  const [accessBlock, setAccessBlock] = React.useState<'trial_ended' | null>(null);

  React.useEffect(() => {
    fetchMe()
      .then((me) => {
        setAuthed(Boolean(me));
        if (me?.user?.id) {
          void hydrateColorSchemePreference(me.user.id).catch(() => {
            // Keep the local theme when the preference endpoint is unavailable.
          });
        }
        setFieldOnly(me?.platform_access === 'field_only');
        setAccessBlock(accessBlockFromCode(me ? null : getLastMeAuthFailureCode()));
        setChecked(true);
      })
      .catch(() => {
        setAuthed(false);
        setAccessBlock(accessBlockFromCode(getLastMeAuthFailureCode()));
        setChecked(true);
      });
  }, []);

  if (!checked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-app-bg">
        <PageLoadingState />
      </div>
    );
  }
  if (accessBlock) return <Navigate to={`/login?reason=${accessBlock}`} replace />;
  if (!authed) {
    return (
      <Navigate
        to={loginPathWithNext(location.pathname, location.search, location.hash)}
        replace
      />
    );
  }
  if (fieldOnly) return <FieldOnlyNotice />;
  return (
    <CompanyTimezoneProvider instanceKey={instanceId ?? meInstanceKeyFallback()}>
      <PdfMailShareProvider instanceId={instanceId ?? meInstanceKeyFallback()}>{children}</PdfMailShareProvider>
    </CompanyTimezoneProvider>
  );
}

function accessBlockFromCode(code: string | null): 'trial_ended' | null {
  if (code === 'trial_ended') return code;
  return null;
}

function meInstanceKeyFallback(): string | null {
  try {
    return localStorage.getItem('activeInstanceId');
  } catch {
    return null;
  }
}

function requireCap(capability: string, areaLabel: string, page: React.ReactNode) {
  return (
    <ProtectedRoute>
      <CapabilityGate capability={capability} areaLabel={areaLabel}>
        {page}
      </CapabilityGate>
    </ProtectedRoute>
  );
}

const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);

export default function App() {
  return (
    <>
    <RouteDocumentTitle />
    <SentryRoutes>
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<LoginPage initialAuthState="register" />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="/auth/confirm" element={<AuthConfirmPage />} />
      <Route path="/auth/link-expired" element={<ExpiredAuthLinkPage />} />
      <Route path="/auth/complete-profile" element={<CompleteProfilePage />} />
      <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
      <Route path="/pending-approval" element={<PendingApprovalPage />} />

      <Route path="/i/:instanceId" element={<ProtectedRoute><InstanceHomeRedirect /></ProtectedRoute>} />
      <Route path="/i/:instanceId/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
      <Route path="/i/:instanceId/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/i/:instanceId/chat" element={requireCap('web.kichat.view', 'KI Chat', <ChatPage />)} />
      <Route path="/i/:instanceId/chat/:chatId" element={requireCap('web.kichat.view', 'KI Chat', <ChatPage />)} />
      <Route path="/i/:instanceId/projects/pipeline" element={requireCap('web.projekte.view', 'Projekte', <LeadsPage />)} />
      <Route path="/i/:instanceId/projects/list" element={requireCap('web.projekte.view', 'Projekte', <LeadsPage />)} />
      <Route path="/i/:instanceId/projects/dashboard" element={requireCap('web.projekte.view', 'Projekte', <LeadsPage />)} />
      <Route path="/i/:instanceId/projects" element={requireCap('web.projekte.view', 'Projekte', <ProjectsDefaultRedirect />)} />
      <Route path="/i/:instanceId/leads" element={requireCap('web.projekte.view', 'Projekte', <LegacyLeadsListRedirect />)} />
      <Route path="/i/:instanceId/contacts" element={requireCap('web.kontakte.view', 'Kontakte', <ContactsPage />)} />
      <Route path="/i/:instanceId/contacts/:id" element={requireCap('web.kontakte.view', 'Kontakte', <ContactDetailPage />)} />
      <Route path="/i/:instanceId/organizations/:id" element={requireCap('web.kontakte.view', 'Kontakte', <OrganizationDetailPage />)} />
      <Route path="/i/:instanceId/inbox/:emailId" element={requireCap('web.posteingang.view', 'Posteingang', <InboxPage />)} />
      <Route path="/i/:instanceId/inbox" element={requireCap('web.posteingang.view', 'Posteingang', <InboxPage />)} />
      <Route path="/i/:instanceId/calendar" element={requireCap('web.plantafel.view', 'Plantafel', <Navigate to="../calendar/calendar" replace />)} />
      <Route path="/i/:instanceId/calendar/:mode" element={requireCap('web.plantafel.view', 'Plantafel', <CalendarPage />)} />
      <Route path="/i/:instanceId/projects/:id/offerings/:offeringId" element={requireCap('web.projekte.view', 'Projekte', <LeadDetailRoute />)} />
      <Route path="/i/:instanceId/projects/:id/offerings/:offeringId/:docType" element={requireCap('web.projekte.view', 'Projekte', <LeadDetailRoute />)} />
      <Route path="/i/:instanceId/projects/:id" element={requireCap('web.projekte.view', 'Projekte', <LeadDetailRoute />)} />
      <Route path="/i/:instanceId/projects/:id/:docType" element={requireCap('web.projekte.view', 'Projekte', <LeadDetailRoute />)} />
      <Route path="/i/:instanceId/leads/:id/:docType" element={requireCap('web.projekte.view', 'Projekte', <LegacyLeadsDetailRedirect />)} />
      <Route path="/i/:instanceId/leads/:id" element={requireCap('web.projekte.view', 'Projekte', <LegacyLeadsDetailRedirect />)} />
      <Route path="/i/:instanceId/ausschreibungen" element={requireCap('web.ausschreibungen.view', 'Ausschreibungen', <AusschreibungenPage />)} />
      <Route path="/i/:instanceId/ausschreibungen/:id" element={requireCap('web.ausschreibungen.view', 'Ausschreibungen', <AusschreibungDetailPage />)} />
      <Route path="/i/:instanceId/todos" element={requireCap('web.aufgaben.own', 'To-dos', <AufgabenPage />)} />
      <Route path="/i/:instanceId/todos/:viewSlug" element={requireCap('web.aufgaben.own', 'To-dos', <AufgabenPage />)} />
      <Route path="/i/:instanceId/aufgaben" element={<LegacyAufgabenRedirect />} />
      <Route path="/i/:instanceId/aufgaben/:viewSlug" element={<LegacyAufgabenRedirect />} />
      <Route path="/i/:instanceId/fleet" element={requireCap('web.fuhrpark.view', 'Fuhrpark', <FuhrparkPage />)} />
      <Route path="/i/:instanceId/fleet/:vehicleId" element={requireCap('web.fuhrpark.view', 'Fuhrpark', <FuhrparkPage />)} />
      <Route path="/i/:instanceId/fleet/:vehicleId/:tabSlug" element={requireCap('web.fuhrpark.view', 'Fuhrpark', <FuhrparkPage />)} />
      <Route path="/i/:instanceId/settings" element={<ProtectedRoute><Navigate to={`../${SETTINGS_PATH_BUSINESS.slice(1)}`} replace /></ProtectedRoute>} />
      <Route path="/i/:instanceId/settings/invoice-template/:templateId" element={<ProtectedRoute><DocumentLayoutEditorPage /></ProtectedRoute>} />
      <Route path="/i/:instanceId/settings/*" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/i/:instanceId/stammdaten" element={requireCap('web.stammdaten.view', 'Stammdaten', <StammdatenDefaultRedirect />)} />
      <Route path="/i/:instanceId/stammdaten/vorlagen/:vorlageId" element={requireCap('web.stammdaten.view', 'Stammdaten', <StammdatenPage />)} />
      <Route path="/i/:instanceId/stammdaten/:tabSlug" element={requireCap('web.stammdaten.view', 'Stammdaten', <StammdatenTabRedirect />)} />
      <Route path="/i/:instanceId/templates" element={requireCap('web.vorlagen.view', 'Vorlagen', <TemplatesPage />)} />
      <Route path="/i/:instanceId/mitarbeiter" element={requireCap('web.hr.view', 'Mitarbeiter', <EmployeesDefaultRedirect />)} />
      <Route
        path="/i/:instanceId/mitarbeiter/uebersicht"
        element={requireCap('web.hr.view', 'Mitarbeiter', <EmployeesOverviewTabRedirect />)}
      />
      <Route
        path="/i/:instanceId/mitarbeiter/uebersicht/:overviewTab"
        element={requireCap('web.hr.view', 'Mitarbeiter', <EmployeesPage />)}
      />
      <Route path="/i/:instanceId/mitarbeiter/:employeeId" element={requireCap('web.hr.view', 'Mitarbeiter', <EmployeesPage />)} />
      <Route path="/i/:instanceId/mitarbeiter/:employeeId/:tab" element={requireCap('web.hr.view', 'Mitarbeiter', <EmployeesPage />)} />
      <Route path="/i/:instanceId/invoices" element={requireCap('web.rechnungen.view', 'Rechnungen', <InvoicesDefaultRedirect />)} />
      <Route path="/i/:instanceId/invoices/:tabSlug" element={requireCap('web.rechnungen.view', 'Rechnungen', <InvoicesPage />)} />
      <Route path="/i/:instanceId/rechnungen" element={requireCap('web.rechnungen.view', 'Rechnungen', <LegacyRechnungenRedirect />)} />
      <Route path="/i/:instanceId/rechnungen/:tabSlug" element={requireCap('web.rechnungen.view', 'Rechnungen', <LegacyRechnungenTabRedirect />)} />
    </SentryRoutes>
    </>
  );
}
